/**
 * Route table. Shell screens are lazy; play and community screens come from their barrels.
 * Keep the paths stable: the other agents link to them.
 */
import { lazy, type ComponentType, type LazyExoticComponent, type ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";

const L = <T extends object>(loader: () => Promise<T>, name: keyof T): LazyExoticComponent<ComponentType> =>
  lazy(async () => ({ default: (await loader())[name] as unknown as ComponentType }));

export const ROUTES = {
  home: "/",
  signIn: "/signin",
  createAccount: "/create-account",
  setup: "/setup",
  practice: "/practice",
  you: "/you",
  stats: "/you/stats",
  history: "/you/history",
  badges: "/you/badges",
  ladder: "/you/ladder",
  frame: "/you/frame",
  items: "/you/items",
  invite: "/you/invite",
  settings: "/you/settings",
  settingsAccount: "/you/settings/account",
  settingsAppearance: "/you/settings/appearance",
  settingsSound: "/you/settings/sound",
  settingsHelp: "/you/settings/help",
  settingsNotifications: "/you/settings/notifications",
  settingsDevices: "/you/settings/devices",
  settingsPrivacy: "/you/settings/privacy",
  learn: "/learn",
  shop: "/shop",
  occasion: "/occasion",
  wallet: "/wallet",
  send: "/send",
  transfer: "/transfer",
  table: "/table",
  lobby: "/lobby",
  results: "/results",
  friends: "/friends",
  clubs: "/clubs",
  leaderboard: "/leaderboard",
  tournaments: "/tournaments",
  admin: "/admin",
} as const;

export interface RouteDef { path: string; element: LazyExoticComponent<ComponentType>; auth: boolean }
export const routeTable: RouteDef[] = [
  ...(import.meta.env.DEV ? [{ path: "/dev/reactions", element: L(() => import("./screens/dev/ReactionsKit"), "ReactionsKitScreen"), auth: false }] : []),
  { path: "/signin", element: L(() => import("./screens/SignIn"), "SignInScreen"), auth: false },
  // D-193: the front door for a first visit; lessons 1–3 need no account (the rest ask to sign in inside the lesson route).
  { path: "/welcome", element: L(() => import("./screens/Welcome"), "WelcomeScreen"), auth: false },
  { path: "/create-account", element: L(() => import("./screens/CreateAccount"), "CreateAccountScreen"), auth: false },
  // D-182: password reset by a verified email.
  // D-212: public pages, signed out and signed in.
  { path: "/privacy", element: L(() => import("./screens/Privacy"), "PrivacyPolicyScreen"), auth: false },
  { path: "/support", element: L(() => import("./screens/Support"), "SupportScreen"), auth: false },
  { path: "/forgot-password", element: L(() => import("./screens/ResetPassword"), "ForgotPasswordScreen"), auth: false },
  { path: "/reset-password", element: L(() => import("./screens/ResetPassword"), "ResetPasswordScreen"), auth: false },
  { path: "/", element: L(() => import("./screens/Home"), "HomeScreen"), auth: true },
  { path: "/setup", element: L(() => import("./screens/SetupTable"), "SetupTableScreen"), auth: true },
  { path: "/practice", element: L(() => import("./screens/SetupTable"), "PracticeScreen"), auth: true },
  { path: "/you", element: L(() => import("./screens/You"), "YouScreen"), auth: true },
  { path: "/you/stats", element: L(() => import("./screens/Stats"), "StatsScreen"), auth: true },
  { path: "/you/history", element: L(() => import("./screens/History"), "HistoryScreen"), auth: true },
  { path: "/you/badges", element: L(() => import("./screens/Badges"), "BadgesScreen"), auth: true },
  { path: "/you/ladder", element: L(() => import("./screens/Ladder"), "LadderScreen"), auth: true },
  { path: "/you/frame", element: L(() => import("./screens/FrameScreen"), "FrameScreen"), auth: true },
  { path: "/you/items", element: L(() => import("./screens/Items"), "ItemsScreen"), auth: true },
  { path: "/you/invite", element: L(() => import("./screens/Invite"), "InviteScreen"), auth: true },
  { path: "/you/settings", element: L(() => import("./screens/Settings"), "SettingsScreen"), auth: true },
  { path: "/you/settings/account", element: L(() => import("./screens/SettingsAccount"), "AccountScreen"), auth: true },
  // D-173: the link in the verification email (api/src/email.ts) opens the account page with its token.
  { path: "/verify-email", element: lazy(async () => ({ default: VerifyEmailRedirect })), auth: true },
  { path: "/you/settings/appearance", element: L(() => import("./screens/SettingsAppearance"), "AppearanceScreen"), auth: true },
  { path: "/you/settings/sound", element: L(() => import("./screens/SettingsSound"), "SoundScreen"), auth: true },
  { path: "/you/settings/help", element: L(() => import("./screens/SettingsHelp"), "HelpScreen"), auth: true },
  { path: "/you/settings/notifications", element: L(() => import("./screens/SettingsNotifications"), "NotificationsScreen"), auth: true },
  { path: "/you/settings/devices", element: L(() => import("./screens/SettingsDevices"), "DevicesScreen"), auth: true },
  { path: "/you/settings/privacy", element: L(() => import("./screens/SettingsPrivacy"), "PrivacyScreen"), auth: true },
  { path: "/learn", element: L(() => import("./screens/learn/LearnScreen"), "LearnScreen"), auth: false },
  { path: "/learn/:lesson", element: L(() => import("./screens/learn/LessonScreen"), "LessonScreen"), auth: false },
  { path: "/shop", element: L(() => import("./screens/Shop"), "ShopScreen"), auth: true },
  // D-180: an occasion pack's page (claim, buy or use it).
  { path: "/occasion/:id", element: L(() => import("./screens/OccasionScreen"), "OccasionScreen"), auth: true },
  { path: "/wallet", element: L(() => import("./screens/Wallet"), "WalletScreen"), auth: true },
  { path: "/send", element: L(() => import("./screens/SendBoli"), "SendBoliScreen"), auth: true },
  { path: "/send/:publicId", element: L(() => import("./screens/SendBoli"), "SendBoliScreen"), auth: true },
  { path: "/transfer/:id", element: L(() => import("./screens/SendBoli"), "TransferStatusScreen"), auth: true },
  { path: "/table/:code", element: L(() => import("./screens/play"), "TableScreen"), auth: true },
  { path: "/lobby/:code", element: L(() => import("./screens/play"), "LobbyScreen"), auth: true },
  { path: "/results/:code", element: L(() => import("./screens/play"), "ResultsScreen"), auth: true },
  { path: "/review/:key", element: L(() => import("./screens/play"), "ReviewScreen"), auth: true },
  { path: "/friends/*", element: L(() => import("./screens/community"), "FriendsRoutes"), auth: true },
  { path: "/people/*", element: L(() => import("./screens/community"), "PeopleRoutes"), auth: true },
  { path: "/players/*", element: L(() => import("./screens/community"), "PeopleRoutes"), auth: true },
  { path: "/clubs/*", element: L(() => import("./screens/community"), "ClubsRoutes"), auth: true },
  { path: "/leaderboard", element: L(() => import("./screens/community"), "LeaderboardScreen"), auth: true },
  { path: "/tournaments/*", element: L(() => import("./screens/community"), "TournamentsRoutes"), auth: true },
  { path: "/prize-checks/:id", element: L(() => import("./screens/community"), "PrizeReportScreen"), auth: true },
  { path: "/admin/*", element: L(() => import("./screens/community"), "AdminRoutes"), auth: true },
];

/** /verify-email?token=… → the account page, which confirms the token (D-173: the email's link used to fall to Home). */
function VerifyEmailRedirect(): ReactElement {
  const { search } = useLocation();
  return <Navigate to={`/you/settings/account${search}`} replace />;
}
